import React from "react";
import styled from "styled-components";
import { Dropdown } from "react-bootstrap";
import { colors } from "utils";
import { lcCountLabel, lcTooltipItems } from "utils/groupCardTitle";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { useQuickGroupActions } from "./useQuickGroupActions";
import { ReleaseGroupCodeModal } from "./ReleaseGroupCodeModal";

// Bucketed session-length options (minutes) — same set the lesson group card
// offers. Quick groups default to 15.
const SESSION_LENGTH_OPTIONS = [
  { label: "10-15 Minutes", value: 10 },
  { label: "15-20 Minutes", value: 15 },
  { label: "20-25 Minutes", value: 20 },
  { label: "25-30 Minutes", value: 25 },
  { label: "30-35 Minutes", value: 30 },
];
const sessionLengthLabel = (value) =>
  SESSION_LENGTH_OPTIONS.find((o) => o.value === Math.round(value))?.label ||
  (Number.isFinite(value) ? `${value} Minutes` : "15-20 Minutes");

const formatUpdated = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US");
};

// No overflow:hidden here — it would clip the Session Length dropdown menu when
// it opens. The card's border-radius still rounds its own background/border, and
// the dark Header rounds its own top corners (below), so the visual is unchanged.
const Card = styled.div`
  background: #fff;
  border: 1px solid ${colors.blue400};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  align-items: center;
  background: ${colors.nightlyWoods500};
  border-radius: 16px 16px 0 0;
  color: #fff;
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
`;

const HeaderLeft = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  & .label {
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
  }
  & .code {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.25px;
    line-height: 26px;
  }
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  padding: 0;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

// Duplicate is the one card action that can fail in a way the teacher must see:
// archive failures mean nothing happened, but a half-done duplicate leaves a real
// group behind.
const ErrorNote = styled.p`
  color: #ba4a21;
  font-size: 14px;
  line-height: 20px;
  margin: 0;
`;

const StandardCode = styled.div`
  color: #ba4a21;
  font-family: "ABC Marfa", sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 20px;
  text-transform: uppercase;
`;

const TitleRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`;

// Clamped to three lines like the bundle cards, which is what makes the hover
// necessary in the first place.
const Title = styled.div`
  color: ${colors.nightlyWoods500};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  flex: 1;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
  min-width: 0;
  overflow: hidden;
`;

// Standard code on the left, the LC count pushed to the right edge — the same row
// the bundle cards use for their track tag + count.
const CodeRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

// The auto margin lives on the tooltip's wrapper span, which is what actually sits
// in the flex row.
const LcCountSlot = styled.span`
  display: inline-flex;
  margin-left: auto;
`;

const LcCount = styled.span`
  color: ${colors.nightlyWoods500};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
`;

const LcTipRow = styled.span`
  display: block;

  & + & {
    margin-top: 8px;
  }
`;

const LcTipLabel = styled.strong`
  font-weight: 700;
`;

// Header ⋮ button and title pencil — transparent icon buttons. `$onDark` styles
// the ⋮ for the dark header; the pencil sits on the white body.
const IconButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${(props) => (props.$onDark ? "#fff" : colors.nightlyWoods500)};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  padding: 0;
`;

const MenuToggle = React.forwardRef(({ children, onClick }, ref) => (
  <IconButton
    $onDark
    type="button"
    ref={ref}
    aria-label="Quick group actions"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </IconButton>
));

// Scopes the ⋮ menu styling to this card (Figma 3914-14466): white menu,
// blue/300 border, 6px radius, blue/50 hover — overriding the page's default
// react-bootstrap dropdown styles.
const MenuScope = styled.div`
  display: inline-flex;

  .dropdown-menu {
    border: 1px solid ${colors.blue300};
    border-radius: 6px;
    box-shadow: 0px 4px 12px rgba(14, 26, 49, 0.12);
    min-width: 176px;
    overflow: hidden;
    padding: 0;
  }
  .dropdown-item {
    color: ${colors.nightlyWoods500};
    font-family: "Inter", sans-serif;
    font-size: 14px;
    line-height: 20px;
    padding: 8px;
  }
  .dropdown-item:hover,
  .dropdown-item:focus,
  .dropdown-item.active {
    background: #f4f4fd;
    color: ${colors.nightlyWoods500};
  }
`;

const Updated = styled.div`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const PreviewLink = styled.button`
  align-self: flex-start;
  background: transparent;
  border: none;
  color: ${colors.blue700};
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  padding: 4px 0;
  text-decoration: underline;
`;

const SessionRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const SessionLabel = styled.div`
  align-items: center;
  color: ${colors.nightlyWoods500};
  display: flex;
  gap: 4px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const CopyIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const InfoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const EllipsisIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

// Same pencil shape as the lesson "Edit Lesson" CTA (AssignmentAccordion
// EditLessonIcon / EditFocusIcon), with currentColor for the card body.
const PencilIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 5L19 9M21.1739 6.81189C21.7026 6.28332 21.9997 5.56636 21.9998 4.81875C21.9999 4.07113 21.703 3.3541 21.1744 2.82539C20.6459 2.29668 19.9289 1.99961 19.1813 1.99951C18.4337 1.99942 17.7166 2.29632 17.1879 2.82489L3.84193 16.1739C3.60975 16.4054 3.43805 16.6904 3.34193 17.0039L2.02093 21.3559C1.99509 21.4424 1.99314 21.5342 2.01529 21.6217C2.03743 21.7092 2.08285 21.7891 2.14673 21.8529C2.21061 21.9167 2.29055 21.962 2.37809 21.984C2.46563 22.006 2.55749 22.0039 2.64393 21.9779L6.99693 20.6579C7.3101 20.5626 7.59511 20.392 7.82693 20.1609L21.1739 6.81189Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Card for a quick group in the Quick Groups list section (Figma 3805-32338).
// Copy code, Preview Questions, editable Session Length, the title pencil
// (opens the Create Quick Group flow), and the ⋮ menu's Archive Group action
// (Figma 3914-14466 menu -> 4253-25373 "Release this group code?" confirm) are
// functional.
export const QuickGroupCard = ({ quickGroup, onPreview, onDeleted, onDuplicated, classroomId }) => {
  const { code, standard_code: standardCode, standard_name: standardName, question_count: questionCount, question_filter: questionFilter, question_set_id: questionSetId, last_updated: lastUpdated } = quickGroup;

  const {
    setSize,
    learningComponents,
    copied,
    copyShareLink,
    sessionValue,
    savingSession,
    handleSessionChange,
    duplicating,
    duplicateError,
    handleDuplicate,
    confirmOpen,
    setConfirmOpen,
    deleting,
    handleRelease,
    handleEdit,
  } = useQuickGroupActions({ quickGroup, classroomId, onDeleted, onDuplicated });

  // The standard always titles the card; the LC filter shows as a count beside the
  // code, hovering the full LC titles (Figma 4914-72908).
  const lcCount = lcCountLabel(learningComponents);
  const cardTitle = standardName;
  const tooltipItems = lcTooltipItems(learningComponents);

  const updated = formatUpdated(lastUpdated);

  return (
    <Card>
      <Header>
        <HeaderLeft>
          <span className="label">Group Codes:</span>
          <span className="code">{(code || "").toUpperCase()}</span>
          <CopyButton
            type="button"
            onClick={copyShareLink}
            aria-label={`Copy share link for group code ${code}`}
            title={copied ? "Copied!" : "Copy group code link"}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </CopyButton>
        </HeaderLeft>
        <MenuScope>
          <Dropdown>
            <Dropdown.Toggle as={MenuToggle} id={`quick-group-menu-${code}`}>
              <EllipsisIcon />
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item onClick={handleDuplicate} disabled={duplicating}>
                {duplicating ? "Duplicating…" : "Duplicate Group"}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setConfirmOpen(true)}>
                Archive Group
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </MenuScope>
      </Header>
      <Body>
        {duplicateError && <ErrorNote role="alert">{duplicateError}</ErrorNote>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <CodeRow>
            <StandardCode>{standardCode}</StandardCode>
            {lcCount && (
              <LcCountSlot>
                <Tooltip
                  id={`${code}-lc-tooltip`}
                  tip={tooltipItems.map(({ label, name }) => (
                    <LcTipRow key={label}>
                      <LcTipLabel>{label}</LcTipLabel> {name}
                    </LcTipRow>
                  ))}
                  placement="right"
                  maxWidth={280}
                >
                  <LcCount
                    tabIndex={0}
                    aria-describedby={`${code}-lc-tooltip`}
                  >
                    {lcCount}
                  </LcCount>
                </Tooltip>
              </LcCountSlot>
            )}
          </CodeRow>
          <TitleRow>
            <Title>{cardTitle}</Title>
            <IconButton type="button" onClick={handleEdit} aria-label={`Edit standard for group code ${code}`}>
              <PencilIcon />
            </IconButton>
          </TitleRow>
          {updated && <Updated>Last updated: {updated}</Updated>}
          {onPreview && (
            <PreviewLink type="button" onClick={onPreview}>
              {/* Same labelling the bundle cards use: a saved set wins over a filter,
                  and question_count is filter-aware server-side now, so it can be
                  shown in every state. */}
              {questionSetId
                ? `My Question Set${Number.isFinite(setSize) ? ` (${setSize})` : ""}`
                : questionFilter
                  ? `Filtered Questions${Number.isFinite(questionCount) ? ` (${questionCount})` : ""}`
                  : `Preview ${Number.isFinite(questionCount) ? `${questionCount} ` : ""}Questions`}
            </PreviewLink>
          )}
        </div>
        <SessionRow>
          <SessionLabel>
            <InfoIcon />
            Session Length:
          </SessionLabel>
          <div style={{ width: 175, ...(savingSession || !code ? { opacity: 0.6, pointerEvents: "none" } : null) }}>
            <SelectDropdown
              ariaLabel="Session Length"
              value={sessionValue}
              displayText={sessionLengthLabel(sessionValue)}
              options={SESSION_LENGTH_OPTIONS}
              onChange={handleSessionChange}
            />
          </div>
        </SessionRow>
      </Body>

      <ReleaseGroupCodeModal
        isOpen={confirmOpen}
        releasing={deleting}
        onClose={() => setConfirmOpen(false)}
        onRelease={handleRelease}
      />
    </Card>
  );
};
